import React, { useEffect, useState, useMemo } from "react";
import SummaryApi from "../common";
import { useNavigate } from "react-router-dom";
import displayINRCurrency from "../helpers/displayCurrency";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { FaFileExcel, FaEye, FaEyeSlash } from "react-icons/fa6";
import scrollTop from "../helpers/scrollTop";
import Modal from "react-modal";

const AllStudents = () => {
  const [students, setStudents] = useState([]);
  const [form, setForm] = useState({
    student_id: "",
    student_admission_number: "",
    full_name: "",
    mobile_number: "",
    email_id: "",
    course: "",
    fee: "",
    payment_method: "",
    payment_date: "",
    other_charges: "",
    transaction_id: "",
    invoice_number: "",
    total_amount: "",
  });
  const [editingIndex, setEditingIndex] = useState(null);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const studentsPerPage = 10;
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [showTotal, setShowTotal] = useState(false);

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      const response = await fetch(SummaryApi.getAllStudents.url);
      const result = await response.json();

      if (result.status && Array.isArray(result.data)) {
        setStudents(result.data);
      } else {
        console.error("Unexpected response:", result);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    const uppercaseFields = [
      "full_name",
      "transaction_id",
      "student_admission_number",
    ];
    const updatedValue = uppercaseFields.includes(name)
      ? value.toUpperCase()
      : value;

    const updatedForm = {
      ...form,
      [name]: updatedValue,
    };

    const fee = parseFloat(name === "fee" ? updatedValue : form.fee) || 0;
    const otherCharges =
      parseFloat(
        name === "other_charges" ? updatedValue : form.other_charges
      ) || 0;
    updatedForm.total_amount = fee + otherCharges;

    setForm(updatedForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const api =
      editingIndex !== null
        ? SummaryApi.updateStudent
        : SummaryApi.createStudent;

    try {
      const response = await fetch(api.url, {
        method: api.method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...form }),
      });

      const result = await response.json();
      if (result.status) {
        await fetchStudents();
        toast.success("Student Form Submitted Successfully!");
        setForm({
          student_id: "",
          student_admission_number: "",
          full_name: "",
          mobile_number: "",
          email_id: "",
          course: "",
          fee: "",
          payment_method: "",
          payment_date: "",
          other_charges: "",
          transaction_id: "",
          invoice_number: "",
          total_amount: "",
        });
        setEditingIndex(null);
        setModalOpen(false);
      } else {
        toast.error(result.message || "Error");
        console.error("API error:", result.message || result);
      }
    } catch (error) {
      toast.error("Submission error");
      console.error("Submission error:", error);
    }
  };

  const handleEdit = (index) => {
    const student = filteredStudents[index];
    setForm({
      student_id: student.student_id,
      student_admission_number: student.student_admission_number,       
      full_name: student.full_name,
      mobile_number: student.mobile_number,
      email_id: student.email_id,
      course: student.course,
      fee: student.fee,
      payment_method: student.payment_method || "",
      payment_date: student.payment_date || "",
      other_charges: student.other_charges || "",
      invoice_number: student.invoice_number || "",
      total_amount: student.total_amount || "",
    });
    setEditingIndex(index);
    setModalOpen(true);
    scrollTop();
  };

  const handleInvoice = (student) => {
    navigate("/invoice", { state: { student } });
    scrollTop();
  };

  const filteredStudents = useMemo(
    () =>
      students.filter((student) => {
        const searchValue = search.toLowerCase();
        return (
          student.student_admission_number
            ?.toLowerCase()
            .includes(searchValue) ||
          student.mobile_number?.includes(searchValue)
        );
      }),
    [students, search]
  );

  const totalReceivedAmount = useMemo(() => {
    return filteredStudents.reduce(
      (total, student) => total + parseFloat(student.total_amount || 0),
      0
    );
  }, [filteredStudents]);

  const indexOfLastStudent = currentPage * studentsPerPage;
  const indexOfFirstStudent = indexOfLastStudent - studentsPerPage;
  const currentStudents = filteredStudents.slice(
    indexOfFirstStudent,
    indexOfLastStudent
  );

  const totalPages = Math.ceil(filteredStudents.length / studentsPerPage);

  const handleDownloadExcel = () => {
    const data = filteredStudents.map(
      (
        {
          student_admission_number,
          full_name,
          father_name,
          mobile_number,
          email_id,
          course,
          year,
          semester,
          fee,
          payment_method,
          payment_date,
          other_charges,
          transaction_id,
          invoice_number,
          total_amount,
        },
        index
      ) => ({
        "S.No": index + 1,
        "Transaction ID": transaction_id,
        "Invoice Number": invoice_number,
        "Student Admission Number": student_admission_number,
        "Student Name": full_name,
        "Father Name": father_name,
        Mobile: mobile_number,
        Email: email_id,
        Course: course,
        Year: year,
        Semester: semester,
        Fee: fee,
        "Other Charges": other_charges,
        "Total Amount": total_amount,
        "Payment Method": payment_method,
        "Payment Date": payment_date,
      })
    );

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Students");
    XLSX.writeFile(workbook, "StudentReports.xlsx");
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  return (
    <div className="container mx-auto p-6">
      <Modal
        isOpen={modalOpen}
        onRequestClose={() => setModalOpen(false)}
        contentLabel="Student Form"
        className="bg-white rounded-lg p-6 max-w-4xl mx-auto mt-20 outline-none shadow-lg"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-start z-50"
      >
        <h2 className="text-xl text-center font-semibold mb-6">
          {editingIndex !== null ? "Edit Student" : "Add Student"}
        </h2>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-8">
          <div>
            <h3 className="text-lg font-bold text-blue-700 mb-4 border-b pb-2">
              Student Details
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              <div>
                <label className="block mb-1 font-medium">
                  Admission Number
                </label>
                <input
                  type="text"
                  name="student_admission_number"
                  value={form.student_admission_number}
                  onChange={handleChange}
                  className="border p-2 rounded w-full"
                />
              </div>
              <div>
                <label className="block mb-1 font-medium">
                  Full Name<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="full_name"
                  value={form.full_name}
                  onChange={handleChange}
                  className="border p-2 rounded w-full"
                  required
                />
              </div>

              <div>
                <label className="block mb-1 font-medium">
                  Mobile Number<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="mobile_number"
                  value={form.mobile_number}
                  onChange={handleChange}
                  maxLength={10}
                  minLength={10}
                  className="border p-2 rounded w-full"
                  required
                />
              </div>
              <div>
                <label className="block mb-1 font-medium">Email</label>
                <input
                  type="email"
                  name="email_id"
                  value={form.email_id}
                  onChange={handleChange}
                  className="border p-2 rounded w-full"
                />
              </div>
              <div>
                <label className="block mb-1 font-medium">
                  Course<span className="text-red-500">*</span>
                </label>
                <select
                  name="course"
                  value={form.course}
                  onChange={handleChange}
                  className="border p-2 rounded w-full"
                  required
                >
                  <option value="">Select Course</option>
                  <option value="CSE">CSE</option>
                  <option value="AI & ML">AI & ML</option>
                  <option value="Aeronautical Engineering">
                    Aeronautical Engineering
                  </option>
                  <option value="ECE">ECE</option>
                  <option value="BBA LLB">BBA LLB</option>
                  <option value="BA LLB">BA LLB</option>
                  <option value="BBA">BBA</option>
                  <option value="BSc Food Science & Tech">
                    BSc Food Science & Tech
                  </option>
                </select>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-bold text-green-700 mb-4 border-b pb-2">
              Payment Details
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              <div>
                <label className="block mb-1 font-medium">
                  Fee<span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="fee"
                  value={form.fee}
                  onChange={handleChange}
                  className="border p-2 rounded w-full"
                  required
                  min={0}
                />
              </div>
              <div>
                <label className="block mb-1 font-medium">Total Amount</label>
                <input
                  type="number"
                  name="total_amount"
                  value={form.total_amount}
                  disabled
                  className="border p-2 rounded w-full bg-yellow-100 text-red-700 font-bold"
                />
              </div>
              <div>
                <label className="block mb-1 font-medium">
                  Payment Method<span className="text-red-500">*</span>
                </label>
                <select
                  name="payment_method"
                  value={form.payment_method}
                  onChange={handleChange}
                  className="border p-2 rounded w-full"
                  required
                >
                  <option value="">Payment Method</option>
                  <option value="Cash">Cash</option>
                  <option value="Card">Card</option>
                  <option value="UPI">UPI</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                </select>
              </div>

              <div>
                <label className="block mb-1 font-medium">Transaction ID</label>
                <input
                  type="text"
                  name="transaction_id"
                  value={form.transaction_id}
                  onChange={handleChange}
                  className="border p-2 rounded w-full"
                />
              </div>
            </div>
          </div>

          <div className="text-right mt-4">
            <button
              type="submit"
              className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
            >
              {editingIndex !== null ? "Update Student" : "Add Student"}
            </button>
          </div>
        </form>
      </Modal>

      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold mb-4">All Students</h3>
        <button
          onClick={() => navigate("/")}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm w-full sm:w-auto"
        >
          + Add Student
        </button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-4 w-full">
          <div className="w-full sm:w-96">
            <input
              type="text"
              placeholder="Search by Student Admission Number or Mobile Number"
              className="border p-2 rounded w-full"
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <button
          onClick={handleDownloadExcel}
          className="bg-emerald-600 hover:bg-emerald-700 w-48 text-white py-2 px-4 rounded flex justify-center items-center gap-1"
        >
          <FaFileExcel /> Download Excel
        </button>
      </div>

      <div className="overflow-x-auto">
        <div className="h-[600px] overflow-y-auto">
          <table className="min-w-full bg-white border shadow rounded text-sm">
            <thead className="bg-gray-100 text-left">
              <tr>
                {[
                  "S NO",
                  "Transaction ID",
                  "Invoice Number",
                  "Admission Number",
                  "Full Name",
                  "Father Name",
                  "Mobile Number",
                  "Email",
                  "Course",
                  "Year & Sem",
                  "Fee",
                  "Total Amount",
                  "Payment Method",
                  "Payment Date",
                  "Action",
                  "Invoice",
                ].map((head, idx) => (
                  <th
                    key={idx}
                    className="px-4 py-2 border-b whitespace-nowrap"
                  >
                    {head}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredStudents.length === 0 ? (
                <tr>
                  <td colSpan="15" className="text-center py-4 text-gray-500">
                    No students found.
                  </td>
                </tr>
              ) : (
                currentStudents.map((student, index) => (
                  <tr
                    key={student.student_id || index}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-4 py-2 border-b">
                      {indexOfFirstStudent + index + 1}
                    </td>
                    <td className="px-4 py-2 border-b max-w-[150px] truncate">
                      {student.transaction_id || "N/A"}
                    </td>
                    <td className="px-4 py-2 border-b max-w-[150px] truncate">
                      {student.invoice_number}
                    </td>
                    <td className="px-4 py-2 border-b max-w-[150px] truncate">
                      {student.student_admission_number || "N/A"}
                    </td>
                    <td className="px-4 py-2 border-b max-w-[180px] truncate">
                      {student.full_name}
                    </td>
                    <td className="px-4 py-2 border-b max-w-[180px] truncate">
                      {student.father_name}
                    </td>
                    <td className="px-4 py-2 border-b max-w-[120px] truncate">
                      {student.mobile_number}
                    </td>
                    <td className="px-4 py-2 border-b max-w-[200px] truncate">
                      {student.email_id}
                    </td>
                    <td className="px-4 py-2 border-b max-w-[150px] truncate">
                      {student.course}
                    </td>
                    <td className="px-4 py-2 border-b max-w-[150px] truncate">
                      {student.year}-{student.semester} sem
                    </td>
                    <td className="px-4 py-2 border-b">
                      {displayINRCurrency(student.fee)}
                    </td>

                    <td className="px-4 py-2 border-b">
                      {displayINRCurrency(student.total_amount)}
                    </td>
                    <td className="px-4 py-2 border-b">
                      {student.payment_method}
                    </td>
                    <td className="px-4 py-2 border-b">
                      {student.payment_date
                        ? formatDate(student.payment_date)
                        : "N/A"}
                    </td>
                    <td className="px-4 py-2 border-b">
                      <button
                        onClick={() => handleEdit(indexOfFirstStudent + index)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm w-full sm:w-auto"
                      >
                        Edit
                      </button>
                    </td>
                    <td className="px-4 py-2 border-b">
                      <button
                        onClick={() => handleInvoice(student)}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-1 rounded-md text-sm"
                      >
                        Invoice
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="mt-5">
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 flex-wrap">
              <button
                className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>

              <div className="text-base font-semibold">
                Page {currentPage} of {totalPages}
              </div>
              <button
                className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>
<div className="flex justify-between">
    <div className="text-base font-semibold">
                Total Records:{" "}
                <span className="ml-1">{filteredStudents.length}</span>
              </div>

              {/* <div className="flex items-center gap-2 text-base font-semibold">
                <span>Total Amount Received:</span>
                {showTotal ? (
                  <>
                    <span className="font-bold text-green-700">
                      {displayINRCurrency(totalReceivedAmount)}
                    </span>
                    <FaEyeSlash
                      className="cursor-pointer"
                      onClick={() => setShowTotal(false)}
                    />
                  </>
                ) : (
                  <>
                    <span className="font-bold">********</span>
                    <FaEye
                      className="cursor-pointer"
                      onClick={() => setShowTotal(true)}
                    />
                  </>
                )}
              </div> */}
</div>
        {/* <div className="relative mt-4">
          <div className="flex justify-between items-center">
            <div className="flex  items-center gap-4">
              <div className="text-base font-semibold">
                Total Records:{" "}
                <span className="ml-1">{filteredStudents.length}</span>
              </div>

              <div className="flex items-center gap-2 text-base font-semibold">
                <span>Total Amount Received:</span>
                {showTotal ? (
                  <>
                    <span className="font-bold text-green-700">
                      {displayINRCurrency(totalReceivedAmount)}
                    </span>
                    <FaEyeSlash
                      className="cursor-pointer"
                      onClick={() => setShowTotal(false)}
                    />
                  </>
                ) : (
                  <>
                    <span className="font-bold">********</span>
                    <FaEye
                      className="cursor-pointer"
                      onClick={() => setShowTotal(true)}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default AllStudents;
